import type {
  ChannelLocationStatusDto,
  Location,
} from '@up2stay/up2promo-api-client';

import { isChannelLocationSyncFailed } from '@/core/lib/channel-location-sync';
import {
  getLocationConnectionStatus,
  GOOGLE_REVIEW_PROVIDER,
  type LocationConnectionStatus,
} from '@/core/lib/review-channel-setup';

export type LocationLinksHealth = 'ok' | 'empty';

export type LocationHealthSummary = {
  google: LocationConnectionStatus;
  links: LocationLinksHealth;
  /** Review channel sync failed — same interrupt as the Status Board. */
  syncFailed: boolean;
};

/** Aggregated synced reviews for a location (all providers). */
export type LocationReviewStats = {
  averageRating: number;
  totalReviews: number;
};

export function formatLocationReviewSummary(
  stats: LocationReviewStats,
  template: string,
  locale: string,
): string {
  return template
    .replace('{rating}', stats.averageRating.toFixed(1))
    .replace('{count}', stats.totalReviews.toLocaleString(locale));
}

export function getLocationLinksHealth(
  location: Pick<Location, 'externalLinks'>,
): LocationLinksHealth {
  const hasLink = (location.externalLinks ?? []).some(
    (entry) => !!entry.value?.trim(),
  );
  return hasLink ? 'ok' : 'empty';
}

export function getLocationHealthSummary(
  location: Pick<
    Location,
    'id' | 'reviewExternalIds' | 'reviewChannelOptOut' | 'externalLinks'
  >,
  channelLocations: ChannelLocationStatusDto[],
): LocationHealthSummary {
  const syncFailed = channelLocations.some(
    (channel) =>
      channel.locationId === location.id &&
      isChannelLocationSyncFailed(channel),
  );
  return {
    google: getLocationConnectionStatus(
      location,
      channelLocations,
      GOOGLE_REVIEW_PROVIDER,
    ),
    links: getLocationLinksHealth(location),
    syncFailed,
  };
}

export type LocationHealthScope = {
  /** Google/sync interrupts. Off on plans without `feature.reputation`. */
  includeReputation?: boolean;
};

function includesReputationHealth(scope?: LocationHealthScope): boolean {
  return scope?.includeReputation !== false;
}

/**
 * Strong Status Board interrupt (full-card Signal Red).
 * Review sync failures and listing gaps — empty links use badge interrupt only
 * so brand-new locations are not painted red by default.
 */
export function locationNeedsAction(
  health: LocationHealthSummary,
  scope?: LocationHealthScope,
): boolean {
  if (!includesReputationHealth(scope)) return false;
  return health.syncFailed || health.google === 'needs_action';
}

/**
 * Triage queue membership — sync failures, review gaps, or missing guest links.
 * Used for roll-up counts, filter, and broken-first sort.
 */
export function locationNeedsAttention(
  health: LocationHealthSummary,
  scope?: LocationHealthScope,
): boolean {
  return locationNeedsAction(health, scope) || health.links === 'empty';
}

/** Sort key: sync/review gaps first, then empty links, then healthy. */
export function locationAttentionRank(
  health: LocationHealthSummary | undefined,
  scope?: LocationHealthScope,
): number {
  if (!health) return 3;
  if (includesReputationHealth(scope)) {
    if (health.syncFailed) return 0;
    if (health.google === 'needs_action') return 1;
  }
  if (health.links === 'empty') return 2;
  return 3;
}
